import React, { useEffect, useRef, useState, useMemo } from 'react'
import VisibilitySensor from 'react-visibility-sensor'
import dynamic from 'next/dynamic'
import { observer } from 'mobx-react-lite'
import { useRouter } from 'next/router'
import MediaQuery from 'components/MediaQuery'

import find from 'lodash/find'

import { storyDetailEvents } from 'lib/ga'
import StoriesStore from 'stores/stories'
import PlaylistsStore from 'stores/playlists'
import { useElementSize, useStore } from 'hooks'
import { THEME, capitalizeAndDashCase } from 'utils'
import StoryHtml from 'components/StoryHtml'
import StoryMeta from 'components/StoryMeta'
import StoryTags from 'components/StoryTags'
import VideoPlayer from 'components/VideoPlayer'
import PodcastPlayer from 'components/PodcastPlayer'
import NextVideoOverlay from 'components/NextVideoOverlay'
import VideoList from 'components/VideoList'
import RevcontentWidget from 'components/RevcontentWidget'
import LazyLoad from 'components/LazyLoad'
import ClientOnly from 'components/ClientOnly'
const SpotIMPopularInTheCommunity = dynamic(() => import('components/SpotIMPopularInTheCommunity'), { ssr: false })
const StoryComments = dynamic(() => import('components/SpotIMComments'), { ssr: false })

const VideoDetail = ({ title, type = 'story', adSettings }) => {
  const router = useRouter()
  const { query } = router
  const storiesStore = useStore(StoriesStore)
  const playlistsStore = useStore(PlaylistsStore)
  const videoSource = storiesStore.currentVideoSource
  const articleRef = useRef(null)
  const articleRefSize = useElementSize(articleRef)
  const [nextVideo, setNextVideo] = useState(null)
  const [countdown, setCountdown] = useState(5)
  const [countdownIntervalCallbackId, setCountdownIntervalCallbackId] = useState(null)

  const isPlaylist = useMemo(() => type === 'playlists', [type])

  const story = useMemo(() => {
    if (isPlaylist && playlistsStore.current) {
      const playlist = playlistsStore.current
      if (query.video) {
        const overrideStory = find(playlist.stories, { id: Number(query.video) })
        if (overrideStory) return overrideStory
      }
      return playlist.stories[0]
    } else {
      return storiesStore.current
    }
  }, [query.video, playlistsStore.current, storiesStore.current, isPlaylist])

  const hideComments = !story.comment_status_open

  const loadMore = async () => {
    if (!isPlaylist) {
      const before = storiesStore.videoList[storiesStore.videoList.length - 1].date
      await storiesStore.findVideoList({ brand: story.brand_id, before })
    }
  }

  // function that starts countdown to play the next video, assuming the next video is picked
  const countdownToPlayback = () => {
    // clear the previous setInterval function from continuing to run
    clearInterval(countdownIntervalCallbackId)

    // start the clock
    setCountdown(5)
    let _countdown = 5

    // start a new countdown interval callback
    let newIntervalFuncId = setInterval(() => {
      if (_countdown === 1) {
        // this countdown has completed.
        clearInterval(newIntervalFuncId)

        // video hasn't been picked, back out
        if (!nextVideo) return

        if (isPlaylist && playlistsStore.current) {
          // push it to the next video in the playlist
          let url = `/playlist/${playlistsStore.current.id}/${playlistsStore.current.slug}`
          router.push(`/playlist/[id]/[slug]?video=${nextVideo.id}&autoplay=true`, {
            pathname: url,
            query: { autoplay: true, video: nextVideo.id }
          })
        } else {
          // push it to the next video in the loadMore queue
          let url = `/video/${nextVideo.id}/${nextVideo.slug}`
          router.push('/video/[id]/[slug]?autoplay=true', { pathname: url, query: { autoplay: true } })
        }
      } else {
        // countdown hasn't completed, decrement the countdown and revisit on the next interval
        setCountdown((currentVal) => currentVal - 1)
        --_countdown
      }
    }, 1000)

    // save the new interval function as state, in case the countdown needs to be stopped elsewhere
    setCountdownIntervalCallbackId(newIntervalFuncId)
  }

  // Starts the countdown for the next video and returns true if there is a next video
  // returns false if there is no next video
  const playNextVideo = () => {
    const videos = storiesStore.videoList
    const currentVideoIndex = videos.indexOf(videos.find((vid) => vid.id === storiesStore.current.id))

    // something went wrong, can't find the current video in the current video list, next video cannot be determined
    if (currentVideoIndex === -1) return false

    const _nextVideo = videos[currentVideoIndex + 1]
    if (_nextVideo?.id) {
      setNextVideo(_nextVideo)
      return true
    }
    return false
  }

  const handleCountdownVisibility = (visible) => {
    if (!visible) {
      // countdown layover is out of the screen, pause the countdown
      clearInterval(countdownIntervalCallbackId)
      setCountdownIntervalCallbackId(null)
    } else {
      // countdown layover is visible again, restart the countdown to the nextVideo that is already picked
      countdownToPlayback()
    }
  }

  useEffect(() => {
    if (query.autoplay) {
      storiesStore.setConsecutiveViewCount(storiesStore.consecutiveViewCount + 1)
      const videoId = story.post_type_meta?.barstool_original?.id
      const videoTitle = story.title
      const viewCount = storiesStore.consecutiveViewCount
      const session = storiesStore.consecutiveViewingSession
      storyDetailEvents.videoAutoplayed(videoId, videoTitle, viewCount, session)
    } else {
      storiesStore.setConsecutiveViewCount(0)
      storiesStore.startConsecutiveViewingSession()
    }

    setNextVideo(null)
    setCountdown(5)
    if (storiesStore.videoList.length) {
      loadMore()
    }
  }, [story])

  useEffect(() => {
    const copyIntervalId = countdownIntervalCallbackId
    return () => {
      clearInterval(copyIntervalId)
    }
  }, [story, countdownIntervalCallbackId])

  return (
    <>
      <div className='container'>
        <div className='row row--main'>
          <div className='col col--main'>
            <article ref={articleRef} className='story'>
              <header className='story__header'>
                <h1 className='story__title'>{story.title}</h1>
                <StoryMeta story={story} placement='top' video />
              </header>
              <div className='video-container'>
                <div className='video'>
                  {nextVideo && (
                    <VisibilitySensor onChange={handleCountdownVisibility}>
                      <NextVideoOverlay
                        countdown={countdown}
                        nextVideo={nextVideo}
                        paused={!countdownIntervalCallbackId}
                      />
                    </VisibilitySensor>
                  )}
                  <VideoPlayer
                    story={story}
                    videoSource={videoSource}
                    playNextVideo={playNextVideo}
                    autoplay={router.query.autoplay || query.t || false}
                    startTime={query.t}
                  />
                </div>
              </div>
              <div className='story__content'>
                {story.type === 'video_podcast' && (
                  <ClientOnly>
                    <PodcastPlayer story={story} />
                  </ClientOnly>
                )}
                <StoryHtml story={story} adSettings={adSettings} />
                <StoryTags tags={story.tags} />
              </div>
              <div>
                <MediaQuery maxWidth={THEME.BREAKPOINTS.SM - 1}>
                  <VideoList
                    title={title}
                    items={storiesStore.videoList}
                    currentItem={story}
                    articleHeight={articleRefSize.height}
                    loadMore={loadMore}
                    isLoading={storiesStore.loading}
                    showButton={storiesStore.videoList.length % 25 === 0}
                    type={type}
                    adSettings={adSettings}
                  />
                </MediaQuery>
              </div>
              <div id='story-comments'>
                {!story.ads_disabled && <RevcontentWidget id={`${capitalizeAndDashCase(story.type)}_Comments-Top`} />}
                <div className='storyComments-container'>
                  <LazyLoad offset={500} once>
                    <SpotIMPopularInTheCommunity />
                    {!hideComments && <StoryComments story={story} />}
                  </LazyLoad>
                </div>
                {!story.ads_disabled && (
                  <RevcontentWidget id={`${capitalizeAndDashCase(story.type)}_Comments-Bottom`} />
                )}
              </div>
            </article>
          </div>
          <div className='col col--sidebar'>
            <MediaQuery minWidth={THEME.BREAKPOINTS.SM}>
              <VideoList
                title={title}
                items={storiesStore.videoList}
                currentItem={story}
                articleHeight={articleRefSize.height}
                loadMore={loadMore}
                isLoading={storiesStore.loading}
                showButton={storiesStore.videoList.length % 25 === 0}
                type={type}
                adSettings={adSettings}
              />
            </MediaQuery>
          </div>
        </div>
      </div>
      <style jsx>{`
        .container {
          padding: 0;
        }
        .row {
          margin-left: 0;
          margin-right: 0;
        }
        .col {
          padding-left: 0;
          padding-right: 0;
        }
        .col--sidebar {
          display: none;
          padding-left: 0;
          padding-right: 0;
        }
        .story {
          background-color: white;
          box-shadow: ${THEME.BOX_SHADOW.DEFAULT};
        }
        .story__title {
          font-size: 20px;
          line-height: 25px;
          font-weight: bold;
          letter-spacing: -0.1px;
          margin: 18px 0 0 0;
          margin: 0;
          padding: 8px 18px;
        }
        .video {
          position: relative;
          padding: 0;
        }
        .story__content {
          padding: 16px;
        }
        @media (min-width: ${THEME.BREAKPOINTS.SM}px) {
          .col--sidebar {
            display: block;
          }
          .story__title {
            font-size: 32px;
            line-height: 40px;
          }
          .video-container {
            padding: 16px 32px;
          }
          .story__content {
            padding: 16px 32px;
          }
          .storyComments-container {
            padding: 4px 16px;
          }
        }
      `}</style>
    </>
  )
}

export default observer(VideoDetail)
